import { Grid } from '@mui/material';
import React, { useEffect, useState, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/pdf.worker.entry';
import { createUseStyles } from 'react-jss';

pdfjs.GlobalWorkerOptions.workerSrc = window.pdfWorker;

const PDF_TYPE = 'application/pdf';
const IMAGE_TYPE = 'image/';

const useStyles = createUseStyles({
  image: {
    width: '100%',
  },
  scrollContainer: {
    width: '100%',
    overflowY: 'auto',
    border: '1px solid black',
  },
  pageWrapper: {
    display: 'flex',
    justifyContent: 'center',
    '&:not(:last-child)': {
      borderBottom: '1px solid #e0e0e0',
    },
  },
});

type AttachmentViewerProps = {
  doc?: string;
  docType?: string;
  /** Stable identifier for the document; remounts the PDF viewer when it changes. */
  docId?: string;
  /** When true, renders every page of a PDF in a scrollable container. Defaults to false (first page only). */
  scrollable?: boolean;
  /** Max height for the scrollable container. Defaults to '80vh'. Only used when scrollable is true. */
  maxHeight?: number | string;
};

const AttachmentViewer: React.FC<AttachmentViewerProps> = ({
  doc,
  docType,
  docId,
  scrollable = false,
  maxHeight = '80vh',
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const prevDocIdRef = useRef<string | undefined>();
  const [numPages, setNumPages] = useState<number>(0);
  const classes = useStyles();

  useEffect(() => {
    if (prevDocIdRef.current !== undefined && prevDocIdRef.current !== docId) {
      setNumPages(0);
    }
    prevDocIdRef.current = docId;
  }, [docId]);

  if (!doc || !docType) {
    return null;
  }

  const docData = `data:${docType};base64,${doc.replace(/(\r\n|\n|\r)/gm, '')}`;

  const onDocumentLoadSuccess = (pdf: { numPages: number }) => {
    setNumPages(pdf.numPages);
  };

  if (docType === PDF_TYPE) {
    if (scrollable) {
      return (
        <Grid item width="100%" ref={ref} className={classes.scrollContainer} style={{ maxHeight }}>
          <Document key={docId} file={docData} onLoadSuccess={onDocumentLoadSuccess}>
            {Array.from({ length: numPages }, (_, index) => (
              <div key={`page_${index + 1}`} className={classes.pageWrapper}>
                <Page pageNumber={index + 1} width={ref.current?.clientWidth} />
              </div>
            ))}
          </Document>
        </Grid>
      );
    }

    return (
      <Grid item width="100%" ref={ref} border={'1px solid black'}>
        <Document key={docId} file={docData} onLoadSuccess={onDocumentLoadSuccess}>
          {numPages > 0 ? (
            <Page pageNumber={1} width={ref.current?.clientWidth} height={ref.current?.clientHeight} />
          ) : null}
        </Document>
      </Grid>
    );
  }

  if (docType.startsWith(IMAGE_TYPE)) {
    return (
      <Grid item width="100%" ref={ref} border={'1px solid black'}>
        <img src={docData} className={classes.image} />
      </Grid>
    );
  }

  return null;
};

export default AttachmentViewer;
